import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Calendar, Clock, FileText, ArrowRight } from "lucide-react";
import { EmailCaptureModal } from "@/components/home/EmailCaptureModal";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import coverEn from "@/assets/report/cover-en.png";
import tocEn from "@/assets/report/toc-en.png";
import coverDe from "@/assets/report/cover-de.png";
import tocDe from "@/assets/report/toc-de.png";

const reportImages = {
  en: { cover: coverEn, toc: tocEn },
  de: { cover: coverDe, toc: tocDe },
};

const reportPreviewDimensions = {
  width: 1357,
  height: 1920,
} as const;

export function ReportSection() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const currentLang = (i18n.language?.split("-")[0] || "en") as "en" | "de" | "fr" | "it" | "es";
  const reportLanguage = currentLang === "de" ? "de" : "en";
  const isDe = currentLang === "de";
  const isFr = currentLang === "fr";
  const isIt = currentLang === "it";
  const isEs = currentLang === "es";
  const images = reportImages[reportLanguage];

  const topics = t("insights.topics", { returnObjects: true }) as string[];

  return (
    <section className="py-24 md:py-32 bg-muted">
      <div className="container">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 text-center">
            <Badge variant="secondary" className="mb-4">
              {t("insights.badge")}
            </Badge>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("insights.reportTitle")}
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">
              {t("insights.reportSubtitle")}
            </p>
          </div>

          <div className="grid gap-8 md:gap-12 lg:grid-cols-2 items-center">
            {/* Report page previews */}
            <div className="flex justify-center gap-4 md:gap-6">
              <button
                onClick={() => setZoomedImage(images.cover)}
                className="relative w-[42%] max-w-[200px] rotate-[-2deg] transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-zoom-in"
              >
                <div className="absolute -inset-2 rounded-lg bg-primary/10 blur-xl" />
                <img
                  src={images.cover}
                  alt={t("insights.reportTitle")}
                  width={reportPreviewDimensions.width}
                  height={reportPreviewDimensions.height}
                  decoding="async"
                  className="relative rounded-lg border border-border shadow-xl"
                  loading="lazy"
                />
              </button>
              <button
                onClick={() => setZoomedImage(images.toc)}
                className="relative w-[42%] max-w-[200px] rotate-[2deg] mt-6 transition-transform hover:rotate-0 hover:scale-105 duration-300 cursor-zoom-in"
              >
                <div className="absolute -inset-2 rounded-lg bg-primary/5 blur-xl" />
                <img
                  src={images.toc}
                  alt={t("insights.whatsInside")}
                  width={reportPreviewDimensions.width}
                  height={reportPreviewDimensions.height}
                  decoding="async"
                  className="relative rounded-lg border border-border shadow-xl"
                  loading="lazy"
                />
              </button>
            </div>

            {/* Report details */}
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                <Badge>{t("insights.featured")}</Badge>
                <Badge variant="outline">{t("insights.newFor2026")}</Badge>
              </div>

              <div>
                <h3 className="text-xl font-bold text-foreground md:text-2xl">
                  {t("insights.reportTitle")}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("insights.whatsInside")}
                </p>
              </div>

              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {topics.map((topic, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {topic}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {isDe ? "Januar 2026" : isFr ? "janvier 2026" : isIt ? "gennaio 2026" : isEs ? "enero de 2026" : "January 2026"}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {isDe ? "25 Min. Lesezeit" : isFr ? "25 min de lecture" : isIt ? "25 min di lettura" : isEs ? "25 min de lectura" : "25 min read"}
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  {isDe ? "29 Quellen zitiert" : isFr ? "29 sources citées" : isIt ? "29 fonti citate" : isEs ? "29 fuentes citadas" : "29 sources cited"}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                <Button
                  onClick={() => setIsEmailModalOpen(true)}
                  className="gap-2 w-full sm:w-auto"
                >
                  <Download className="h-4 w-4" />
                  {reportLanguage === "en" ? t("common.downloadEN") : t("common.downloadDE")}
                </Button>
                <Button variant="outline" size="sm" className="group w-full sm:w-auto" asChild>
                  <a href={getLocalizedPath("/insights")}>
                    {t("common.viewReportDetails")}
                    <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!zoomedImage} onOpenChange={() => setZoomedImage(null)}>
        <DialogContent className={`max-w-[90vw] sm:max-w-3xl sm:w-auto p-1 sm:p-2 rounded-lg bg-background/95 backdrop-blur-sm border-border ${zoomedImage === images.toc ? "[&>button]:text-white [&>button]:hover:text-white/80" : ""}`}>
          {zoomedImage && (
            <img
              src={zoomedImage}
              alt={t("insights.reportTitle")}
              width={reportPreviewDimensions.width}
              height={reportPreviewDimensions.height}
              loading="eager"
              decoding="async"
              className="max-h-[85vh] w-full sm:w-auto rounded-md"
            />
          )}
        </DialogContent>
      </Dialog>

      <EmailCaptureModal
        open={isEmailModalOpen}
        onOpenChange={setIsEmailModalOpen}
        reportLanguage={reportLanguage}
        source="Home"
      />
    </section>
  );
}
